import * as THREE from "three";

import { createIndexedGeometry } from "./loomNativeViewerHelpers";

export type LoomSurfaceAppearance = "opaque" | "ghost-inspection";

export interface LoomSurfaceMaterialPolicy {
  depthTest: true;
  depthWrite: boolean;
  opacity: number;
  side: THREE.Side;
  sideLabel: "front" | "double";
  transmission: 0;
  transparent: boolean;
}

export const LOOM_GHOST_SURFACE_OPACITY = 0.34;

const OPAQUE_POLICY: LoomSurfaceMaterialPolicy = {
  depthTest: true,
  depthWrite: true,
  opacity: 1,
  side: THREE.FrontSide,
  sideLabel: "front",
  transmission: 0,
  transparent: false,
};

const GHOST_POLICY: LoomSurfaceMaterialPolicy = {
  depthTest: true,
  depthWrite: false,
  opacity: LOOM_GHOST_SURFACE_OPACITY,
  side: THREE.DoubleSide,
  sideLabel: "double",
  transmission: 0,
  transparent: true,
};

export function loomSurfaceMaterialPolicy(
  appearance: LoomSurfaceAppearance,
): LoomSurfaceMaterialPolicy {
  return appearance === "ghost-inspection" ? GHOST_POLICY : OPAQUE_POLICY;
}

export function addLoomHybridSurface(
  group: THREE.Group,
  positions: Float32Array,
  faces: Uint32Array,
  color: string,
  name: string,
  appearance: LoomSurfaceAppearance,
): THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial> {
  const policy = loomSurfaceMaterialPolicy(appearance);
  const material = new THREE.MeshPhysicalMaterial({
    clearcoat: appearance === "opaque" ? 0.32 : 0.08,
    clearcoatRoughness: 0.52,
    color,
    depthTest: policy.depthTest,
    depthWrite: policy.depthWrite,
    envMapIntensity: appearance === "opaque" ? 0.76 : 0.46,
    flatShading: false,
    metalness: 0.06,
    opacity: policy.opacity,
    reflectivity: 0.42,
    roughness: appearance === "opaque" ? 0.4 : 0.52,
    side: policy.side,
    transmission: policy.transmission,
    transparent: policy.transparent,
  });
  material.forceSinglePass = false;
  material.userData = {
    loomFinish: appearance === "opaque" ? "satin-clearcoat" : "ghost-inspection",
    loomInspectionOnly: appearance === "ghost-inspection",
    loomLightReactive: true,
    loomSurfaceAppearance: appearance,
  };
  const mesh = new THREE.Mesh(createIndexedGeometry(positions, faces), material);
  mesh.name = name;
  mesh.renderOrder = appearance === "ghost-inspection" ? 1 : 0;
  group.add(mesh);
  return mesh;
}
